import { useMutation } from '@tanstack/react-query';
import { OverlappingSourcingPurchaseResponse } from 'api-clients/monolith/models/OverlappingSourcingPurchaseResponse';
import _floor from 'lodash/floor';
import _round from 'lodash/round';
import _uniq from 'lodash/uniq';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { validateSourcingPurchases } from '@/api-clients/validate-sourcing-purchases';
import { useToaster } from '@/components/base/toaster';
import { useJobPostingContext } from '@/contexts/job-posting-context';
import { useBudgetType } from '@/hooks/use-budget-type';

import { BudgetType, ChannelFormState } from '../types';

interface UseChannelFormProps {
  channelSlug: string;
  snapshotBeforeEditRef: React.MutableRefObject<ChannelFormState>;
}

export function useChannelForm({
  channelSlug,
  snapshotBeforeEditRef,
}: UseChannelFormProps) {
  const intl = useIntl();
  const { notifyError, notifySuccess } = useToaster();
  const {
    hookForm,
    setSelectedChannelSlugs,
    selectedChannelSlugs,
    setSelectedOpeningIds,
  } = useJobPostingContext();
  const { calculateTotalBudget } = useBudgetType();
  const {
    watch,
    setValue,
    getValues,
    trigger,
    setError,
    clearErrors,
    resetField,
  } = hookForm;
  // Array of opening IDs that have conflicts for a specific channel
  const [openingsWithConflicts, setOpeningsWithConflicts] =
    useState<string[]>();

  const channelBudget = watch(`channels.${channelSlug}.channelBudget`);
  const budgetAllocation = watch(`channels.${channelSlug}.budgetAllocation`);
  const startsAt = watch(`channels.${channelSlug}.startsAt`);
  const endAt = watch(`channels.${channelSlug}.endAt`);
  const budgetType = watch(`channels.${channelSlug}.budgetType`);
  const openingBudgets = getValues(`channels.${channelSlug}.openingBudgets`);

  /* Helper function to calculate the total budget for the channel based on the budget type and duration */
  const getTotalBudget = useCallback(
    (budgetType: BudgetType) => {
      return calculateTotalBudget({
        budgetType,
        budgetPerUnit: channelBudget ?? 0,
        startDate: startsAt,
        endDate: endAt,
      });
    },
    [calculateTotalBudget, channelBudget, startsAt, endAt],
  );

  /* Calculate the total budget for the channel when the budget type, start date or end date changes */
  useEffect(() => {
    setValue(
      `channels.${channelSlug}.totalChannelBudget`,
      getTotalBudget(budgetType),
    );
  }, [budgetType, setValue, channelSlug, getTotalBudget]);

  /* Set the opening budgets to be evenly distributed if the budget allocation is set to evenly */
  useEffect(() => {
    if (channelBudget && budgetAllocation === 'evenly') {
      const perOpeningBudget = _floor(
        channelBudget / Object.keys(openingBudgets).length,
        2,
      );

      const updatedOpeningBudgets = Object.keys(openingBudgets).reduce(
        (acc, openingId) => ({
          ...acc,
          [openingId]: perOpeningBudget,
        }),
        {} as Record<string, number>,
      );

      setValue(`channels.${channelSlug}.openingBudgets`, updatedOpeningBudgets);
    } else if (channelBudget && budgetAllocation === 'per-funnel') {
      void trigger(`channels.${channelSlug}.openingBudgets`);
    }

    /* Adding openingBudgets to the dependency array will cause a infinite loop */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channelBudget, budgetAllocation, setValue, channelSlug]);

  const conflictsErrorMessage = (count: number) =>
    intl.formatMessage(
      {
        defaultMessage:
          '{count, plural, one {1 opening} other {{count} openings}} have campaigns that overlap.',
        description: 'Error message for overlapping sourcing purchases',
      },
      { count },
    );

  const {
    mutate: validateOverlappingPurchases,
    isPending,
    data,
  } = useMutation({
    mutationFn: validateSourcingPurchases,
    onSuccess: (data: OverlappingSourcingPurchaseResponse) => {
      const { conflicts } = data;
      const uniqueOpeningsWithConflicts = _uniq(
        conflicts.map(conflict => conflict.opening_id),
      );

      if (uniqueOpeningsWithConflicts.length > 0) {
        setOpeningsWithConflicts(uniqueOpeningsWithConflicts);

        notifyError({
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
        setError(`channels.${channelSlug}.endAt`, {
          message: conflictsErrorMessage(uniqueOpeningsWithConflicts.length),
        });
      } else {
        notifySuccess({
          message: intl.formatMessage({
            defaultMessage: 'Changes saved successfully',
            description: 'Success message for saving campaign details',
          }),
        });
        setValue(`channels.${channelSlug}.isExpanded`, false);
        setValue(`channels.${channelSlug}.isValidated`, true);

        // Reset the channel form so that any new changes will be captured by the hook form dirty fields
        resetField(`channels.${channelSlug}`, {
          defaultValue: getValues(`channels.${channelSlug}`),
          keepDirty: false,
        });
      }
    },
    onError: () => {
      notifyError({
        message: intl.formatMessage({
          defaultMessage: 'Failed to save changes',
          description: 'Error message for saving campaign details',
        }),
      });
    },
  });

  const conflicts: OverlappingSourcingPurchaseResponse['conflicts'] =
    useMemo(() => {
      const allConflicts = data?.conflicts ?? [];
      // Filter conflicts to only include openings that are still in openingsWithConflicts. We render the conflicts from API and use openingsWithConflicts to manage the state.
      if (openingsWithConflicts) {
        return allConflicts.filter(conflict =>
          openingsWithConflicts.includes(conflict.opening_id),
        );
      }
      return allConflicts;
    }, [data, openingsWithConflicts]);

  /* Validate the channel changes by calling the API to validate if there are any overlapping purchases */
  const validateChannelChanges = async () => {
    const isValid = await trigger(`channels.${channelSlug}`);
    if (!isValid) return;

    validateOverlappingPurchases({
      channel_slug: channelSlug,
      start_date: getValues(`channels.${channelSlug}.startsAt`),
      end_date: getValues(`channels.${channelSlug}.endAt`),
      opening_ids: Object.keys(
        getValues(`channels.${channelSlug}.openingBudgets`),
      ),
    });
  };

  /* Cancel the channel changes by resetting the form to the saved snapshot */
  const cancelChannelChanges = () => {
    void trigger(`channels.${channelSlug}`);
    // Reset the form to the saved snapshot
    setValue(`channels.${channelSlug}`, {
      ...snapshotBeforeEditRef.current,
      isExpanded: false,
    });
  };

  /* Remove the channel from the posting by removing the channel from the form data */
  const removeChannelFromPosting = () => {
    const formData = getValues();
    const {
      channels: { [channelSlug]: _, ...restChannels },
    } = formData;
    setValue('channels', restChannels);
    setSelectedChannelSlugs(prev => prev.filter(slug => slug !== channelSlug));
  };

  /* Validate the channel budget by triggering the channel budget validation */
  const validateChannelBudget = () => {
    void trigger(`channels.${channelSlug}.channelBudget`);
  };

  /* Recalculate the channel budget by summing the opening budgets whenever opening budgets change */
  const recalculateChannelBudget = (
    openings?: Record<string, number | null>,
  ) => {
    const totalBudget = Object.values(
      openings ?? getValues(`channels.${channelSlug}.openingBudgets`),
    ).reduce((acc: number, budget: number | null) => acc + (budget ?? 0), 0);

    setValue(`channels.${channelSlug}.channelBudget`, _round(totalBudget, 2));
    validateChannelBudget();
  };

  /* Remove the opening from the channel when user deletes it */
  const removeOpeningFromChannel = (openingId: string) => {
    const updatedOpenings = { ...openingBudgets };
    delete updatedOpenings[openingId];
    setValue(`channels.${channelSlug}.openingBudgets`, updatedOpenings);
    recalculateChannelBudget(updatedOpenings);

    // If there is only one opening left, set the budget allocation to evenly
    if (Object.keys(updatedOpenings).length === 1)
      setValue(`channels.${channelSlug}.budgetAllocation`, 'evenly');

    // If there is only one channel left, remove the opening from the list of selected openings
    if (selectedChannelSlugs.length === 1)
      setSelectedOpeningIds(prev => prev.filter(id => id !== openingId));
  };

  /* Remove the conflicting opening from the channel when user deletes it */
  const removeConflictingOpening = (openingId: string) => {
    const updatedConflicts = openingsWithConflicts?.filter(
      id => id !== openingId,
    );
    setOpeningsWithConflicts(updatedConflicts);
    removeOpeningFromChannel(openingId);

    if (updatedConflicts && updatedConflicts.length > 0) {
      setError(`channels.${channelSlug}.endAt`, {
        message: conflictsErrorMessage(updatedConflicts?.length ?? 0),
      });
    } else {
      clearErrors(`channels.${channelSlug}.endAt`);
    }
  };

  const clearConflicts = () => {
    setOpeningsWithConflicts(undefined);
  };

  /* Recalculate the total budget when the budget type changes */
  const handleBudgetTypeChange = (budgetType: BudgetType) => {
    const totalBudget = getTotalBudget(budgetType);
    setValue(`channels.${channelSlug}.totalChannelBudget`, totalBudget);
    validateChannelBudget();
  };

  return {
    isPending,
    validateChannelChanges,
    cancelChannelChanges,
    removeChannelFromPosting,
    removeOpeningFromChannel,
    removeConflictingOpening,
    validateChannelBudget,
    recalculateChannelBudget,
    conflicts,
    openingsWithConflicts,
    clearConflicts,
    handleBudgetTypeChange,
  };
}
